import Vue from 'vue';
import Component from 'vue-class-component';
import { COURSE_EVENTS } from '@/content/values/events';
import { Prop } from 'vue-property-decorator';
import { PRESENTATION_MODE_EVENTS } from '@/content/values/ed-presentation-mode.values';
import { VOLUME_LISTENER, EVENTS_TYPE } from '@/content/values/ed-youtube-video.values';
import KEYS from '@/values/keys';
import canAutoplay from 'can-autoplay';
import WebSocketMessageResponse from '@/content/models/web-socket-message-response.model';
import { DISCONNECTED_SOCKET_EVENT } from '@/core/values/app.values';

@Component({})
export default class EdYoutubeVideo extends Vue {

    @Prop() readonly videoId : string;

    private saveInterval : NodeJS.Timeout = null;
    private currentData = {
        currentTime: 0,
        isPaused: false,
        type: '',
        isMuted: false,
        volume: 100
    }
    private processEvents = {
        playing: this.playVideo,
        paused: this.pauseVideo
    }
    private conditionsState : boolean = false;

    get isPresentationModeStudent(){
        return this.$store.state.CourseStore.isPresentationStudentMode;
    }

    get isPresentationModeTeacher(){
        return this.$store.state.CourseStore.isPresentationTeacherMode;
    }

    get player(){
        return this.$refs.youtube['player'];
    }

    get playerVars(){
        return this.isPresentationModeStudent
                ? { controls: 0, disablekb: 1}
                : this.isPresentationModeTeacher
                ? {fs: 0}
                : {};
    }

    get initialPresentationModeMessage() : WebSocketMessageResponse {
        return this.$store.state.MeetingsStore.initialPresentationModeMessage;
    }

    private ready() : void {
        if(this.isPresentationModeStudent){
            this.$edEventBus.$emit(PRESENTATION_MODE_EVENTS.LOAD_PRESENTATION_ELEMENTS);
            this.validateInitialMessage();
        }

        if(this.isPresentationModeTeacher){
            this.addVolumeControl();
            this.$edEventBus.$emit(PRESENTATION_MODE_EVENTS.LOAD_PRESENTATION_ELEMENTS);
        }
        this.$Courses.Store.setVideoLoading(false);
    }

    private playing() : void {
        if(!this.isPresentationModeTeacher){
            return;
        }
        
        clearInterval(this.saveInterval);
        this.sendCurrentTime(false);
        this.saveInterval = setInterval(() => this.sendCurrentTime(false), 4000);
    }

    private paused() : void {
        if(!this.isPresentationModeTeacher){
            return;
        }
        clearInterval(this.saveInterval);
        this.currentData = {...this.currentData, isPaused: true, type: EVENTS_TYPE.paused};
        this.$edEventBus.$emit(COURSE_EVENTS.CHANGE_STATE_VIDEO, this.currentData);
    }

    private validateAutoplay(callback : Function) : void {
        canAutoplay.video().then(({result}) => {
            if(!result){
                this.$EdModal.createMessageModal({
                    title: this.$i18n.t('meetings.presentationMode.autoplayDisabledTitle'),
                    body: this.$i18n.t('meetings.presentationMode.autoplayDisabledMessage'),
                    type: KEYS.EVENT_TYPES.WARNING,
                    action: callback
                });
            }else{
                callback();
            }
        });
    }

    private validateInitialMessage() : void {
        if(this.initialPresentationModeMessage && this.initialPresentationModeMessage.messages['Video']){
            return;
        }
        this.updateStateVideo(null);
    }

    private addVolumeControl(){
        this.player.addEventListener(VOLUME_LISTENER.eventName, VOLUME_LISTENER.listener);
        let _self = this;
        window.onvolumechange = function(event) {
            _self.currentData = {..._self.currentData, isMuted: event.data.muted, volume: event.data.volume};
            _self.$edEventBus.$emit(COURSE_EVENTS.CHANGE_STATE_VIDEO, _self.currentData);
        };
    }

    private async sendCurrentTime(onlySave : boolean) {
        const response = await this.player.getCurrentTime();
        const playerState = await this.player.getPlayerState()

        this.currentData = {
            ...this.currentData,
            isPaused: false,
            currentTime: response,
            type: playerState == 0 ? EVENTS_TYPE.ended : EVENTS_TYPE.playing
        };
        this.$edEventBus.$emit(COURSE_EVENTS.CHANGE_STATE_VIDEO, this.currentData, onlySave);
    }

    private updateStateVideo(data : string) : void {
        if(data === null) {
            return;
        }
        
        let dataParsed = JSON.parse(data);
        if(this.processEvents[dataParsed.type]){
            this.processEvents[dataParsed.type](dataParsed);
        }
    }
    
    private playVideo(data: any){
        if(this.conditionsState){
            this.updatePlayVideo(data);
            return;
        }

        this.validateAutoplay(() => {
            this.updatePlayVideo(data);
            this.conditionsState = true;
        });
    }

    private updatePlayVideo(data: any) : void {
        this.player.playVideo();
        this.player.seekTo(data.currentTime);
        this.player.setVolume(data.volume);
        data.isMuted ? this.player.mute() : this.player.unMute();
    }

    private pauseVideo() : void {
        this.player.pauseVideo();
    }

    mounted() {
        this.$edEventBus.$on(COURSE_EVENTS.UPDATE_STATE_VIDEO, this.updateStateVideo);
        this.$edEventBus.$on(DISCONNECTED_SOCKET_EVENT, this.pauseVideo);
        this.$Courses.Store.setVideoLoading(true);
    }

    beforeDestroy() {
        clearInterval(this.saveInterval);
        this.player.removeEventListener(VOLUME_LISTENER.eventName, VOLUME_LISTENER.listener);
        this.$edEventBus.$off(COURSE_EVENTS.UPDATE_STATE_VIDEO, this.updateStateVideo);
        this.$edEventBus.$off(DISCONNECTED_SOCKET_EVENT, this.pauseVideo);
        if(this.isPresentationModeStudent){
            this.$EdModal.closeModal();
        }
    }
}